import React, { useEffect, useState } from 'react';
import SocialLoginSkeleton from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/socialLoginSkeleton';
import { reportError } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/reportService';
import { apiFetchData } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/apiService';
import {
    dispatchTrackingEvent,
    PROVIDER_LOGIN_PAGE_FACEBOOK_LOGIN_CLICK,
    PROVIDER_LOGIN_PAGE_GOOGLE_LOGIN_CLICK,
    PROVIDER_REGISTRATION_PAGE_EXISTING_USER_CLICK,
    PROVIDER_REGISTRATION_PAGE_FACEBOOK_LOGIN_CLICK,
    PROVIDER_REGISTRATION_PAGE_GOOGLE_LOGIN_CLICK,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/services/tracking';
import { IS_FRAME_SERVICE } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/frameService';
import LocalStorageManipulationService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/LocalStorageManipulationService';

export default function SocialLogin({
    socialLoginsAPIResponse,
    socialLoginsPathExtension: pathExtension,
    redirectURL,
    redirectType,
    isFromApplyPage,
    isFromLoginPage,
    loginType,
    trackingDL,
    changeOnExistingUser,
    changeFormText,
    isSwitchedToLogin,
    onGoogleLoginClick,
    onFacebookLoginClick,
}) {
    const [publicResources, setPublicResources] = useState(socialLoginsAPIResponse || null);
    const [isLoading, setIsLoading] = useState(false);

    const getData = () => {
        const queryParams = {
            redirectURL,
            redirectType,
        };
        const filteredParams = Object.entries(queryParams)
            .filter(([, value]) => value !== '' && value !== null && value !== undefined)
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        const finalParams = filteredParams ? `?${filteredParams}` : '';
        setIsLoading(true);
        apiFetchData(pathExtension, finalParams)
            .then((res) => {
                setPublicResources(res.data);
            })
            .catch((err) => {
                reportError(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        if (!publicResources) {
            getData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleGoogleLoginClick = () => {
        onGoogleLoginClick?.();
        if (!IS_FRAME_SERVICE) {
            if (isFromApplyPage && redirectType === 'provider') {
                dispatchTrackingEvent(PROVIDER_REGISTRATION_PAGE_GOOGLE_LOGIN_CLICK);
            } else if (isFromLoginPage && loginType === 'provider') {
                dispatchTrackingEvent(PROVIDER_LOGIN_PAGE_GOOGLE_LOGIN_CLICK);
            }
            // Šalje se kroz props i privremeno se postavlja u LS.
            // Razlog: Zbog logike prijave/registracije preko google-a i fb-a nemamo mogućnost detektiranja dolazi li sa
            // page-a ili modala pa na ovaj način privremeno spremamo u LS dok ne napunimo DL, nakon toga se uklanja.
            if (trackingDL?.formLoginRegistration) {
                LocalStorageManipulationService.setLastClickedLoginOrRegister(trackingDL.formLoginRegistration);
            }
        }
    };

    const handleFacebookLoginClick = () => {
        onFacebookLoginClick?.();
        if (!IS_FRAME_SERVICE) {
            if (isFromApplyPage && redirectType === 'provider') {
                dispatchTrackingEvent(PROVIDER_REGISTRATION_PAGE_FACEBOOK_LOGIN_CLICK);
            } else if (isFromLoginPage && loginType === 'provider') {
                dispatchTrackingEvent(PROVIDER_LOGIN_PAGE_FACEBOOK_LOGIN_CLICK);
            }
            // Isti komentar kao i za odgovarajući block koda kod handleGoogleLoginClick.
            if (trackingDL?.formLoginRegistration) {
                LocalStorageManipulationService.setLastClickedLoginOrRegister(trackingDL.formLoginRegistration);
            }
        }
    };

    const handleExistingUserClick = () => {
        if (!IS_FRAME_SERVICE && isFromApplyPage && !isSwitchedToLogin && redirectType === 'provider') {
            dispatchTrackingEvent(PROVIDER_REGISTRATION_PAGE_EXISTING_USER_CLICK);
        }
        changeOnExistingUser();
    };

    if (isLoading) {
        return (
            <SocialLoginSkeleton
                contentHeight={15}
                numOfSkeleton={changeFormText ? 3 : 2}
            />
        );
    }

    return (
        <div id="socialLogin" className="social-login">
            {publicResources && (
                <>
                    <a
                        data-tracking-category={publicResources.google.dataTracking.category}
                        data-tracking-action={publicResources.google.dataTracking.action}
                        href={publicResources.google.loginURL}
                        className="social-login__link social-login__link--google"
                        aria-label="google login"
                        onClick={handleGoogleLoginClick}
                    >
                        <svg className="social-login__link-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <rect width="24" height="24" rx="8" />
                            <path d="M21.4057 10.1247C21.5307 10.7942 21.5987 11.4941 21.5987 12.2245C21.5987 17.9376 17.7751 22 12 22C10.6867 22.0004 9.38613 21.742 8.17269 21.2396C6.95925 20.7372 5.8567 20.0006 4.92804 19.072C3.99937 18.1433 3.26279 17.0407 2.76039 15.8273C2.25798 14.6139 1.9996 13.3133 2 12C1.9996 10.6867 2.25798 9.38613 2.76039 8.17269C3.26279 6.95926 3.99937 5.8567 4.92804 4.92804C5.8567 3.99937 6.95925 3.26279 8.17269 2.76039C9.38613 2.25798 10.6867 1.9996 12 2C14.7001 2 16.9563 2.9935 18.6873 4.60666L15.8683 7.42564V7.41853C14.819 6.41893 13.4872 5.90593 12 5.90593C8.70053 5.90593 6.01869 8.69342 6.01869 11.9939C6.01869 15.2934 8.70053 18.087 12 18.087C14.9937 18.087 17.0315 16.3753 17.45 14.0246H12V10.1247H21.4067H21.4057Z" fill="#FF9C1A" />
                        </svg>
                        <span>{publicResources.google.dataTracking.action}</span>
                    </a>
                    <a
                        data-tracking-category={publicResources.facebook.dataTracking.category}
                        data-tracking-action={publicResources.facebook.dataTracking.action}
                        href={publicResources.facebook.loginURL}
                        className="social-login__link social-login__link--facebook"
                        aria-label="facebook login"
                        onClick={handleFacebookLoginClick}
                    >
                        <svg className="social-login__link-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M2 12.0558C2 17.0275 5.61083 21.1617 10.3333 22V14.7775H7.83333V12H10.3333V9.7775C10.3333 7.2775 11.9442 5.88917 14.2225 5.88917C14.9442 5.88917 15.7225 6 16.4442 6.11083V8.66667H15.1667C13.9442 8.66667 13.6667 9.2775 13.6667 10.0558V12H16.3333L15.8892 14.7775H13.6667V22C18.3892 21.1617 22 17.0283 22 12.0558C22 6.525 17.5 2 12 2C6.5 2 2 6.525 2 12.0558Z" fill="#1F4F77" />
                        </svg>
                        <span>{publicResources.facebook.dataTracking.action}</span>
                    </a>
                    {changeFormText && (
                        <button
                            className="social-login__link social-login__link--neutral"
                            type="button"
                            onClick={handleExistingUserClick}
                            disabled={isLoading}
                        >
                            <svg className="social-login__link-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 23.4484C5.68243 23.4484 0.551514 18.3175 0.551514 12C0.551514 5.68243 5.68243 0.551514 12 0.551514C18.3175 0.551514 23.4484 5.68243 23.4484 12C23.4484 18.3175 18.3175 23.4484 12 23.4484Z" fill="white" /><path d="M12 1.08635C18.0167 1.08635 22.9136 5.96657 22.9136 12C22.9136 18.0167 18.0334 22.9136 12 22.9136C5.98329 22.9136 1.08635 18.0334 1.08635 12C1.08635 5.96657 5.96657 1.08635 12 1.08635ZM12 0C5.38162 0 0 5.38162 0 12C0 18.6184 5.38162 24 12 24C18.6184 24 24 18.6184 24 12C24 5.38162 18.6184 0 12 0Z" fill="white" /><path fillRule="evenodd" clipRule="evenodd" d="M8.97512 1.52097C9.96119 1.23684 10.9807 1.08643 12.0002 1.08643C12.2676 1.08643 12.535 1.10314 12.8024 1.11985C16.1116 1.35384 19.0197 3.07528 20.8581 5.63239C19.5378 5.41512 18.1339 5.33155 16.5629 5.49868C16.028 5.54882 15.5099 5.6491 14.9751 5.74938C14.2899 5.8998 13.6046 6.13378 13.0197 6.33434H13.003C12.9027 6.36776 12.0002 6.68531 12.6019 6.55161C14.0559 6.23406 15.5266 6.15049 17.0141 6.31762C18.1673 6.43462 19.2202 6.68531 20.2732 6.9193C20.7913 7.03629 21.3261 7.15328 21.8442 7.25356C22.1283 7.83852 22.3623 8.4569 22.5294 9.092C21.9946 8.97501 21.4431 8.85801 20.9083 8.74102C19.8553 8.50704 18.8191 8.27306 17.7328 8.08921C16.1617 7.8218 14.4904 7.6881 12.6353 7.8218C11.7996 7.88865 10.9807 8.00565 10.1617 8.17278C9.32609 8.33991 8.49044 8.62403 7.75506 8.85801C7.70492 8.87473 7.65478 8.89144 7.58793 8.90815C7.45423 8.95829 6.28431 9.35941 7.06982 9.19228C9.32609 8.72431 11.2481 8.74102 13.0197 8.97501C14.4236 9.15885 15.7272 9.4764 17.0141 9.77723C17.4319 9.87751 17.8331 9.97779 18.2509 10.0614C18.7021 10.1616 19.1367 10.2619 19.5879 10.3455C20.6743 10.546 21.7606 10.6797 22.847 10.7132C22.8971 11.1143 22.9138 11.5154 22.9138 11.9332C22.9138 12.1338 22.9138 12.3343 22.8971 12.5182C21.3762 12.3176 19.9222 12.0168 18.4514 11.6992H18.4347C16.7968 11.3483 15.159 10.9973 13.4208 10.78C10.9138 10.4792 8.22303 10.4458 5.04754 11.131C3.9779 11.365 2.94169 11.716 2.00576 12.0502C1.95562 12.0669 1.92219 12.0836 1.87205 12.1004C1.60464 12.1839 1.33723 12.2842 1.08654 12.4012V12.3678C1.08654 12.2173 1.06982 12.0836 1.06982 11.9332C1.08654 7.01957 4.41244 2.82459 8.97512 1.52097Z" fill="url(#paint0_linear_32130_18236)" /><path d="M12.0002 22.8968C8.85815 22.8968 6.01692 21.5598 4.02806 19.4205C6.58517 18.4846 9.10884 17.5319 12.8191 17.9832C14.0392 18.1336 11.2147 17.5988 10.8136 17.5486C7.87207 17.1977 5.56567 17.4149 3.0587 18.2172C2.62416 17.5821 2.25647 16.8968 1.95564 16.1949C5.76622 15.5932 9.09213 14.3397 13.7718 15.3258C14.1729 15.4094 14.5573 15.5096 14.9584 15.6099C15.8944 15.8773 14.7746 15.4595 13.7885 15.1085C13.3038 14.9414 12.8526 14.791 12.7022 14.7408C9.27597 13.7715 6.51831 13.6378 3.39297 14.1559C2.72444 14.2729 2.07263 14.4066 1.40411 14.5403C1.25369 13.8885 1.15341 13.22 1.10327 12.5347C1.15341 12.5347 1.18684 12.518 1.25369 12.5013C6.26762 11.4149 9.9445 12.2673 13.5545 13.0862C13.6047 13.103 13.6715 13.103 13.7217 13.1197C14.7244 13.3537 15.7272 13.5709 16.7634 13.7548C17.315 13.8551 17.8665 13.9219 18.4347 13.9888C19.8554 14.1559 21.276 14.2227 22.6966 14.1559C22.2788 16.1782 21.3094 17.9832 19.9556 19.4372C18.5852 20.8745 16.847 21.9442 14.8749 22.4957C13.9389 22.7631 12.9863 22.8968 12.0002 22.8968Z" fill="url(#paint1_linear_32130_18236)" /><defs><linearGradient id="paint0_linear_32130_18236" x1="1.08654" y1="6.83598" x2="22.9021" y2="6.83598" gradientUnits="userSpaceOnUse"><stop stopColor="#FF9C1A" /><stop offset="0.066" stopColor="#FFA524" /><stop offset="0.245" stopColor="#FFB93A" /><stop offset="0.443" stopColor="#FFC749" /><stop offset="0.672" stopColor="#FFCF52" /><stop offset="1" stopColor="#FFD255" /></linearGradient><linearGradient id="paint1_linear_32130_18236" x1="1.10327" y1="17.457" x2="22.6799" y2="17.457" gradientUnits="userSpaceOnUse"><stop stopColor="#4499E3" /><stop offset="0.13" stopColor="#469FE8" /><stop offset="0.619" stopColor="#4DAEF7" /><stop offset="1" stopColor="#4FB4FC" /></linearGradient></defs>
                            </svg>
                            <span>{changeFormText || ''}</span>
                        </button>
                    )}
                </>
            )}
        </div>
    );
}
